import clsx from "clsx";
import { CheckCircle2, Info, Loader2, TriangleAlert, X } from "lucide-react";
import { type ReactNode, useRef } from "react";

import { motion, useClosing } from "./presence";
import { IconButton, type IconProp, renderIcon } from "./ui";
import { t } from "../i18n/i18n";
import { ReportAnomalyLink } from "../feedback/reportLink";

/** Tons des encadrés et bandeaux : `danger` (erreur, échec — jamais d'orange ni de texte rouge nu pour une
 *  erreur), `warn` (risque, avertissement), `info` (consigne, information), `ok` (confirmé). */
export type CalloutTone = "danger" | "warn" | "info" | "ok";

const TONE: Record<CalloutTone, { box: string; icon: string; Icon: typeof Info }> = {
  danger: { box: "bg-danger-soft", icon: "text-danger", Icon: TriangleAlert },
  warn: { box: "bg-warn-soft", icon: "text-warn", Icon: TriangleAlert },
  info: { box: "bg-accent-soft", icon: "text-accent", Icon: Info },
  ok: { box: "bg-ok-soft", icon: "text-ok", Icon: CheckCircle2 },
};

/** Encadré dans le contenu (R10) : message placé près de son déclencheur (erreur d'enregistrement sous le bouton,
 *  consigne sous un champ). Fond pâle du ton, icône de 14 px, texte en 12 px, `title` en semi-gras, `action` à
 *  droite (`Button variant="link"` ou `size="sm"`), croix si `onClose`. Lu aussitôt par les lecteurs d'écran pour
 *  `danger` et `warn` (`role="alert"`). Entre en glissé depuis le haut et sort de même, dans une `Presence`. Le
 *  résultat d'une action passe plutôt par `notify` (avis), l'état durable d'une vue par `Banner`.
 *
 *  Erreur (`danger`) dans l'espace de travail ou l'accueil : lien « Signaler cette anomalie » sous le texte, qui
 *  ouvre la fenêtre de l'anomalie avec ce texte (registre F39) ; `report={false}` pour une erreur de saisie de
 *  l'utilisateur (mot de passe refusé), qui n'est pas une anomalie de Generex. */
export function Callout({
  tone = "info",
  title,
  children,
  action,
  onClose,
  icon,
  className,
  report = true,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  onClose?: () => void;
  /** Icône propre (par défaut celle du ton). */
  icon?: IconProp;
  /** Marges seulement. */
  className?: string;
  /** Erreur : proposer « Signaler cette anomalie » (oui par défaut). */
  report?: boolean;
}) {
  const closing = useClosing();
  const style = TONE[tone];
  const text = useRef<HTMLDivElement>(null);
  return (
    <div
      role={tone === "danger" || tone === "warn" ? "alert" : "status"}
      className={clsx("flex items-start gap-2 rounded-lg px-3 py-2 text-ui text-ink", style.box, motion("top", closing), className)}
    >
      <span className={clsx("flex shrink-0 pt-0.5", style.icon)}>{renderIcon(icon ?? style.Icon, 14)}</span>
      <div className="min-w-0 flex-1 leading-snug break-words">
        <div ref={text}>
          {title && <div className="font-semibold">{title}</div>}
          {children && <div className={clsx(title ? "mt-0.5 text-ink-soft" : undefined)}>{children}</div>}
        </div>
        {tone === "danger" && report && <ReportAnomalyLink source={text} className="mt-1" />}
      </div>
      {action && <div className="flex shrink-0 items-center self-center">{action}</div>}
      {onClose && (
        <IconButton size="xs" onClick={onClose} title={t("Fermer")} aria-label={t("Fermer")} className="-my-0.5 -mr-1">
          <X size={14} />
        </IconButton>
      )}
    </div>
  );
}

/** Tons d'un bandeau : ceux de l'encadré, plus `neutral` (information grise), `accent` (consigne, action
 *  attendue) et `run` (IA ou tâche en cours, roue de chargement). `brand` : ancien nom de `danger`. */
export type BannerTone = CalloutTone | "neutral" | "accent" | "run" | "brand";

const BANNER: Record<Exclude<BannerTone, "brand">, { box: string; icon?: typeof Info }> = {
  danger: { box: "bg-danger-soft text-danger", icon: TriangleAlert },
  warn: { box: "bg-warn-soft text-warn", icon: TriangleAlert },
  info: { box: "bg-accent-soft text-accent", icon: Info },
  ok: { box: "bg-ok-soft text-ok", icon: CheckCircle2 },
  neutral: { box: "bg-canvas text-ink-soft", icon: Info },
  accent: { box: "bg-accent-soft text-accent" },
  run: { box: "bg-run-soft text-run", icon: Loader2 },
};

/** Bandeau d'état durable d'une vue (R10), sous son en-tête : langue de l'ordre différente, rédaction en cours,
 *  document modifié. Pleine largeur, filet en bas, `px-4 py-2.5`, texte en 12 px ; `actions` à droite
 *  (`Button size="sm"`). `icon` : `false` pour aucune icône. Glisse depuis le haut dans une `Presence`. */
export function Banner({
  tone = "neutral",
  icon,
  children,
  actions,
  className,
  report = true,
}: {
  tone?: BannerTone;
  icon?: IconProp | false;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Erreur : proposer « Signaler cette anomalie » sous le texte (oui par défaut, comme `Callout`). */
  report?: boolean;
}) {
  const closing = useClosing();
  const text = useRef<HTMLDivElement>(null);
  const style = BANNER[tone === "brand" ? "danger" : tone];
  const shown = icon === false ? null : (icon ?? style.icon);
  return (
    <div
      role={tone === "danger" || tone === "brand" ? "alert" : "status"}
      className={clsx(
        "flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-2.5 text-ui",
        style.box,
        motion("top", closing),
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2 leading-snug break-words">
        {shown && (
          <span className={clsx("flex shrink-0", tone === "run" && icon === undefined && "animate-spin")}>{renderIcon(shown, 14)}</span>
        )}
        <div className="min-w-0">
          <div ref={text}>{children}</div>
          {(tone === "danger" || tone === "brand") && report && <ReportAnomalyLink source={text} className="mt-0.5" />}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
